import React from 'react';

/**
 * Recurring job configuration component
 * @param {Object} props - Component props
 * @param {Object} props.formData - Current form data
 * @param {Function} props.onChange - Form data change handler
 * @param {boolean} props.isRecurring - Whether recurring job is enabled
 * @param {Function} props.onRecurringChange - Recurring toggle handler
 * @param {Object} props.errors - Validation errors
 * @returns {JSX.Element} RecurringJobConfig component
 */
export default function RecurringJobConfig({ 
  formData, 
  onChange, 
  isRecurring, 
  onRecurringChange, 
  errors = {} 
}) {
  const handleChange = (field, value) => {
    onChange({
      ...formData,
      [field]: value
    });
  };

  const handleRecurringToggle = (checked) => {
    onRecurringChange?.(checked);
    
    // Reset recurring-specific fields when toggling off
    if (!checked) {
      onChange({
        ...formData,
        frequency: 'WEEKLY',
        dayOfWeek: 3, // Wednesday in JavaScript's standard numbering (0=Sunday)
        dayOfMonth: 1, // Default for monthly jobs
        endDate: ''
      });
    }
  };

  return (
    <div className="mb-4">
      <div className="flex items-center mb-2">
        <input
          type="checkbox"
          id="isRecurring"
          checked={isRecurring}
          onChange={(e) => handleRecurringToggle(e.target.checked)}
          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="isRecurring" className="ml-2 text-sm font-medium text-gray-700">
          Make this a recurring job
        </label>
      </div>
      
      {isRecurring && (
        <div className="mt-3 pl-6 border-l-2 border-blue-100 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Frequency *
            </label>
            <select
              value={formData.frequency || 'WEEKLY'}
              onChange={(e) => handleChange('frequency', e.target.value)}
              className="w-full p-2 border rounded-md"
            >
              <option value="DAILY">Daily</option>
              <option value="WEEKLY">Weekly</option>
              <option value="BIWEEKLY">Bi-weekly</option>
              <option value="MONTHLY">Monthly</option>
              <option value="QUARTERLY">Quarterly</option>
            </select>
          </div>
          
          {/* Conditional fields based on frequency */}
          {(formData.frequency === 'WEEKLY' || formData.frequency === 'BIWEEKLY') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Day of Week *
              </label>
              <select
                value={formData.dayOfWeek || 3}
                onChange={(e) => handleChange('dayOfWeek', e.target.value)}
                className={`w-full p-2 border rounded-md ${errors.dayOfWeek ? 'border-red-500' : ''}`}
              >
                <option value="0">Sunday</option>
                <option value="1">Monday</option>
                <option value="2">Tuesday</option>
                <option value="3">Wednesday</option>
                <option value="4">Thursday</option>
                <option value="5">Friday</option>
                <option value="6">Saturday</option>
              </select>
              {errors.dayOfWeek && (
                <p className="mt-1 text-xs text-red-500">{errors.dayOfWeek}</p>
              )}
            </div>
          )}
          
          {formData.frequency === 'MONTHLY' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Day of Month *
              </label>
              <select
                value={formData.dayOfMonth || 1}
                onChange={(e) => handleChange('dayOfMonth', parseInt(e.target.value, 10))}
                className={`w-full p-2 border rounded-md ${errors.dayOfMonth ? 'border-red-500' : ''}`}
              >
                {[...Array(31)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}{i + 1 === 1 ? 'st' : i + 1 === 2 ? 'nd' : i + 1 === 3 ? 'rd' : 'th'} of each month
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Jobs will repeat on this day each month. If a month doesn't have this day (e.g., 31st in February), it will use the last day of that month.
              </p>
              {errors.dayOfMonth && (
                <p className="mt-1 text-xs text-red-500">{errors.dayOfMonth}</p>
              )}
            </div>
          )}
          
          {formData.frequency === 'QUARTERLY' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Day of Month *
              </label>
              <select
                value={formData.dayOfMonth || 1}
                onChange={(e) => handleChange('dayOfMonth', parseInt(e.target.value, 10))}
                className={`w-full p-2 border rounded-md ${errors.dayOfMonth ? 'border-red-500' : ''}`}
              >
                {[...Array(31)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Jobs will be created on this day in January, April, July, and October.
              </p>
              {errors.dayOfMonth && (
                <p className="mt-1 text-xs text-red-500">{errors.dayOfMonth}</p>
              )}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date * <span className="text-xs text-gray-500">(max 1 year)</span>
            </label>
            <input
              type="date"
              value={formData.endDate || ''}
              onChange={(e) => handleChange('endDate', e.target.value)}
              className={`w-full p-2 border rounded-md ${errors.endDate ? 'border-red-500' : ''}`}
              min={new Date().toISOString().split('T')[0]} // Minimum is today
              max={new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]} // Maximum is 1 year from now
            />
            {errors.endDate && (
              <p className="mt-1 text-xs text-red-500">{errors.endDate}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              All jobs for this recurring schedule will be created immediately up to this end date.
            </p>
          </div>
        </div>
      )}
    </div>
  );
} 